/**
 * useOffline Hook - 오프라인 상태 관리
 *
 * 네트워크 상태 감지 및 오프라인 데이터 관리
 */

import { useState, useEffect, useCallback } from 'react';
import {
  savePendingWorkout,
  getPendingWorkouts,
  getCachedExercises,
  getCachedExercisesByBodyPart,
  cacheExercises,
  cacheRoutine,
  getCachedRoutines,
  isIndexedDBSupported,
  getOfflineDataSize,
  type PendingWorkout,
  type CachedExercise,
  type CachedRoutine,
} from '../services/offline/indexedDB';
import {
  initSyncService,
  syncAllPendingWorkouts,
  addSyncListener,
  getPendingCount,
  type SyncEvent,
} from '../services/offline/syncService';

interface OfflineState {
  isOnline: boolean;
  isSupported: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncResult: {
    total: number;
    synced: number;
    failed: number;
  } | null;
}

interface UseOfflineReturn extends OfflineState {
  // 운동 기록 관련
  saveWorkoutOffline: (workout: {
    exerciseId: number;
    sets: number;
    reps: number;
    weight: number;
    workoutDate: string;
    notes?: string;
  }) => Promise<string>;
  getPendingWorkouts: () => Promise<PendingWorkout[]>;

  // 캐시 관련
  cacheExercises: (exercises: CachedExercise[]) => Promise<void>;
  getCachedExercises: (bodyPartId?: number) => Promise<CachedExercise[]>;
  cacheRoutine: (routine: Omit<CachedRoutine, 'cachedAt'>) => Promise<void>;
  getCachedRoutines: () => Promise<CachedRoutine[]>;

  // 동기화 관련
  syncNow: () => Promise<void>;
  refreshPendingCount: () => Promise<void>;

  // 유틸리티
  getDataSize: () => Promise<{
    pendingWorkouts: number;
    cachedExercises: number;
    cachedRoutines: number;
  }>;
}

export function useOffline(): UseOfflineReturn {
  const [state, setState] = useState<OfflineState>({
    isOnline: navigator.onLine,
    isSupported: isIndexedDBSupported(),
    pendingCount: 0,
    isSyncing: false,
    lastSyncResult: null,
  });

  // 네트워크 상태 감지
  useEffect(() => {
    const handleOnline = () => {
      setState((prev) => ({ ...prev, isOnline: true }));
    };

    const handleOffline = () => {
      setState((prev) => ({ ...prev, isOnline: false }));
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync Service 초기화 및 이벤트 리스닝
  useEffect(() => {
    if (!state.isSupported) return;

    initSyncService();

    const unsubscribe = addSyncListener((event: SyncEvent) => {
      switch (event.type) {
        case 'start':
          setState((prev) => ({ ...prev, isSyncing: true }));
          break;
        case 'complete':
          setState((prev) => ({
            ...prev,
            isSyncing: false,
            lastSyncResult: {
              total: event.total,
              synced: event.completed,
              failed: event.failed,
            },
          }));
          // 대기 중인 수 업데이트
          refreshPendingCount();
          break;
        case 'error':
          setState((prev) => ({ ...prev, isSyncing: false }));
          break;
      }
    });

    // 초기 대기 중인 수 로드
    refreshPendingCount();

    return () => {
      unsubscribe();
    };
  }, [state.isSupported]);

  // 대기 중인 운동 수 갱신
  const refreshPendingCount = useCallback(async () => {
    if (!state.isSupported) return;
    try {
      const count = await getPendingCount();
      setState((prev) => ({ ...prev, pendingCount: count }));
    } catch (error) {
      console.error('Failed to get pending count:', error);
    }
  }, [state.isSupported]);

  // 오프라인 운동 저장
  const saveWorkoutOffline = useCallback(
    async (workout: {
      exerciseId: number;
      sets: number;
      reps: number;
      weight: number;
      workoutDate: string;
      notes?: string;
    }) => {
      if (!state.isSupported) {
        throw new Error('Offline storage not supported');
      }

      const id = await savePendingWorkout(workout);
      await refreshPendingCount();
      return id;
    },
    [state.isSupported, refreshPendingCount]
  );

  // 대기 중인 운동 조회
  const getPendingWorkoutsCallback = useCallback(async () => {
    if (!state.isSupported) return [];
    return getPendingWorkouts();
  }, [state.isSupported]);

  // 운동 목록 캐시
  const cacheExercisesCallback = useCallback(
    async (exercises: CachedExercise[]) => {
      if (!state.isSupported) return;
      await cacheExercises(exercises);
    },
    [state.isSupported]
  );

  // 캐시된 운동 목록 조회
  const getCachedExercisesCallback = useCallback(
    async (bodyPartId?: number) => {
      if (!state.isSupported) return [];
      if (bodyPartId !== undefined) {
        return getCachedExercisesByBodyPart(bodyPartId);
      }
      return getCachedExercises();
    },
    [state.isSupported]
  );

  // 루틴 캐시
  const cacheRoutineCallback = useCallback(
    async (routine: Omit<CachedRoutine, 'cachedAt'>) => {
      if (!state.isSupported) return;
      await cacheRoutine(routine);
    },
    [state.isSupported]
  );

  // 캐시된 루틴 조회
  const getCachedRoutinesCallback = useCallback(async () => {
    if (!state.isSupported) return [];
    return getCachedRoutines();
  }, [state.isSupported]);

  // 즉시 동기화
  const syncNow = useCallback(async () => {
    if (!state.isSupported || !state.isOnline) return;

    setState((prev) => ({ ...prev, isSyncing: true }));
    try {
      await syncAllPendingWorkouts();
    } catch (error) {
      console.error('Manual sync failed:', error);
    }
  }, [state.isSupported, state.isOnline]);

  // 데이터 크기 조회
  const getDataSize = useCallback(async () => {
    if (!state.isSupported) {
      return { pendingWorkouts: 0, cachedExercises: 0, cachedRoutines: 0 };
    }
    return getOfflineDataSize();
  }, [state.isSupported]);

  return {
    ...state,
    saveWorkoutOffline,
    getPendingWorkouts: getPendingWorkoutsCallback,
    cacheExercises: cacheExercisesCallback,
    getCachedExercises: getCachedExercisesCallback,
    cacheRoutine: cacheRoutineCallback,
    getCachedRoutines: getCachedRoutinesCallback,
    syncNow,
    refreshPendingCount,
    getDataSize,
  };
}

export default useOffline;
